import { useMemo, useState } from 'react';
import type { FieldDefinition } from '@dunea/plugin-sdk';
import type { AdminCardProps } from '~/components/workbench/admin-extensions/registry';
import { FieldsForm, draftErrors } from '~/components/workbench/fields';
import { apiResourceFields, apiSetMetafield, type ResourceFields } from '~/lib/api/data';
import { workbenchStore } from '~/lib/stores/workbench';
import { classNames } from '~/utils/classNames';
import {
  BTN_GHOST,
  BTN_SECONDARY,
  CAPTION,
  CARD,
  ErrorBanner,
  Spinner,
} from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import {
  CUSTOM_FIELD_TARGETS,
  apiErrorMessage,
  canEditValues,
  changedMetafields,
  metafieldFormKey,
  namespaceLabel,
} from './content-model';
import { createReferenceLoader, qualifyReference } from './references';

/**
 * First-party admin card for product / order / collection / funnel detail
 * views: the pinned custom fields (metafield definitions) for that resource
 * type, edited in place. Renders nothing until the owner has defined a field
 * for this kind of resource (they do that in the Content panel).
 */
export function CustomFieldsCard({ appId, target, resourceId }: AdminCardProps) {
  const ownerType = CUSTOM_FIELD_TARGETS[target];
  const data = useResource(
    ownerType && resourceId ? () => apiResourceFields(appId, ownerType, resourceId) : null,
    `${appId}:${target}:${resourceId ?? ''}`,
  );

  if (!ownerType || !resourceId || data.error || !data.data?.definitions.length) {
    return null;
  }

  return <CustomFieldsForm key={resourceId} appId={appId} resource={data.data} onSaved={data.setData} />;
}

function savedValues(resource: ResourceFields): Record<string, unknown> {
  const out: Record<string, unknown> = {};

  for (const v of resource.values) {
    out[`${v.namespace}__${v.key}`] = v.value;
  }

  return out;
}

function CustomFieldsForm({
  appId,
  resource,
  onSaved,
}: {
  appId: string;
  resource: ResourceFields;
  onSaved: (next: ResourceFields) => void;
}) {
  const saved = useMemo(() => savedValues(resource), [resource]);
  const [values, setValues] = useState<Record<string, unknown>>(saved);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const references = useMemo(() => createReferenceLoader(appId, () => []), [appId]);

  const fields: FieldDefinition[] = resource.definitions.map((d) => {
    const from = namespaceLabel(d.namespace);
    return qualifyReference(
      {
        ...(d.field as FieldDefinition),
        key: metafieldFormKey(d),
        label: from ? `${d.name} (${from.toLowerCase()})` : d.name,
      },
      d.namespace,
    );
  });
  const locked = new Set(resource.definitions.filter((d) => !canEditValues(d)).map(metafieldFormKey));
  const changes = changedMetafields(resource.definitions, saved, values);

  const save = async () => {
    const problems = draftErrors(fields, values);
    setErrors(problems);
    setError(null);

    if (Object.keys(problems).length || !changes.length) {
      return;
    }

    setBusy(true);

    try {
      const next = { ...resource, values: [...resource.values] };

      for (const c of changes) {
        const result = await apiSetMetafield(appId, {
          ownerType: resource.ownerType,
          ownerId: resource.ownerId,
          namespace: c.namespace,
          key: c.key,
          value: c.value,
        });

        next.values = next.values.filter((v) => !(v.namespace === c.namespace && v.key === c.key));

        if (!('deleted' in result)) {
          next.values.push(result);
        }
      }

      onSaved(next);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save the custom fields.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={classNames(CARD, 'space-y-3 p-4')} aria-label="Custom fields">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="i-ph:textbox text-[13px] text-muted" />
          <h4 className={CAPTION}>Custom fields</h4>
        </div>
        <button type="button" className={BTN_GHOST} onClick={() => workbenchStore.openTool('content')}>
          Manage
          <div className="i-ph:arrow-right text-[12px]" />
        </button>
      </div>

      <FieldsForm
        fields={fields.filter((f) => !locked.has(f.key))}
        values={values}
        onChange={setValues}
        errors={errors}
        disabled={busy}
        references={references}
      />

      {error && <ErrorBanner message={error} />}

      {changes.length > 0 && (
        <div className="flex items-center justify-end gap-1.5">
          <button type="button" className={BTN_GHOST} disabled={busy} onClick={() => setValues(saved)}>
            Discard
          </button>
          <button type="button" className={BTN_SECONDARY} disabled={busy} onClick={() => void save()}>
            {busy ? <Spinner /> : <div className="i-ph:check text-[13px]" />}
            Save fields
          </button>
        </div>
      )}
    </section>
  );
}
